<template>
  <div
    class="side-bar"
    :class="{ 'side-bar__shrunk': isShrunk }"
  >
    <div
      class="side-bar__header"
      :class="[{ 'side-bar__header--short': isShrunk && !isMobile, pointer: !isMobile }]"
      @click="!isMobile && setShrunkPreference(!isShrunk)"
    >
      <template>
        <div
          v-if="!isShrunk || isMobile"
          class="logo-wrap"
        >
          <GlBrandLogo
            :class="[{'default-logo': appConfig.VUE_APP_THEME && appConfig.VUE_APP_THEME === 'default' }]"
            :ny="featureAccess('NY')"
          />
        </div>
        <div
          v-else
          class="logo-wrap"
        >
          <GlBrandLogo :variant="isTablet ? 'short-24' : 'short'" />
        </div>
      </template>
      <div class="flex align-center">
        <gl-icon
          v-if="isShrunk && isMobile && $route.name === 'analytics'"
          class="mr-3"
          height="24"
          name="search-graph"
          width="24"
          @click="openMobileSearchOnGraphBar"
        />
        <gl-icon
          v-if="isShrunk && isMobile && $route.name === 'analytics'"
          class="mr-3"
          height="18"
          name="equalizer"
          width="18"
          @click="openMobileViewBar"
        />
        <gl-icon
          v-if="isShrunk && isMobile && $route.name === 'analytics'"
          class="mr-3"
          height="24"
          name="cases"
          width="24"
          @click="openMobileCases"
        />
        <gl-icon
          v-if="isShrunk && isMobile"
          :class="{ 'sidebar-open-icon__shrunk': isShrunk }"
          height="24"
          name="sidebar-open"
          width="24"
          @click="setShrunk(false)"
        />
        <gl-icon
          v-else-if="isMobile"
          height="12"
          name="sidebar-close"
          width="24"
          @click="setShrunk(true)"
        />
      </div>
    </div>
    <div class="side-bar__wrap">
      <div
        class="side-bar__main"
        :class="{
          'side-bar__main--overflow': isOverflow,
          'side-bar__main--scrolled-to-bottom': isScrolledToBottom
        }"
      >
        <SidebarSection
          v-for="section in sidebarSections"
          :key="section.key"
          :has-notify="hasNotify"
          :is-mobile="isMobile"
          :is-shrunk="isShrunk"
          :is-tablet="isTablet"
          :section="section"
          @toggle="toggleSection"
        />
      </div>

      <div class="side-bar__footer">
        <SidebarMenuItem
          v-for="link in visibleFooterLinks"
          :key="link.key"
          :has-notify="hasNotify"
          :is-mobile="isMobile"
          :is-shrunk="isShrunk"
          :is-tablet="isTablet"
          :link="link"
        />
      </div>
    </div>
  </div>
</template>

<script>
// Components
import GlIcon from '@/components/gl-icon'
// Vuex
import {mapActions, mapState, mapMutations} from "vuex";
// Mixins
import deviceWidthMixin from '@/assets/mixins/deviceWidthMixin'
// Utils
import appConfig from "@/utils/appConfig";

import SidebarSection from '@/components/sidePanelMenu/SidebarSection'
import SidebarMenuItem from '@/components/sidePanelMenu/SidebarMenuItem'
import GlBrandLogo from '@/components/gl-brand-logo.vue'
import menuLinksMixin from '@/assets/mixins/menuLinksMixin'

const SIDEBAR_SECTIONS_STORAGE_KEY = 'sidebarOpenedSections'

const getInitialOpenedSections = () => {
  const defaultSections = {
    dashboard: true,
    investigations: true,
    kyt: true,
    kyb: true,
    settings: true,
    admin: true,
  }

  const savedSections = localStorage.getItem(SIDEBAR_SECTIONS_STORAGE_KEY)

  if (!savedSections) return defaultSections

  try {
    return {
      ...defaultSections,
      ...JSON.parse(savedSections),
    }
  } catch (e) {
    localStorage.removeItem(SIDEBAR_SECTIONS_STORAGE_KEY)
    return defaultSections
  }
}

export default {
  name: 'SidepanelMenu',

  components: {
    GlIcon,
    GlBrandLogo,
    SidebarSection,
    SidebarMenuItem,
  },
  mixins: [deviceWidthMixin, menuLinksMixin],
  data() {
    return {
      alertInterval: null,
      notifyList: {},
      isOverflow: false,
      resizeObserver: null,
      isScrolledToBottom: false,
      openedSections: getInitialOpenedSections(),
    }
  },
  computed: {
    appConfig() {
      return appConfig
    },
    ...mapState('sidebar', ['isShrunk']),
    ...mapState('user', ['userData', 'token']),
    sidebarSections() {
      return this.visibleMenuSections.map(section => ({
        ...section,
        isOpen: this.openedSections[section.key],
      }))
    },
  },
  watch: {
    $route: {
      async handler(val) {
        // windowWidth is measured in the mixin's mounted(), which runs after an
        // immediate watcher — and `null <= 767` is true, so without this guard
        // isMobile reads true on first run at every screen size and the sidebar
        // collapses on load even on a desktop. The mounted() hook below covers
        // the genuine small-screen case.
        if (val && this.windowWidth !== null && (this.isMobile || this.isTablet)) {
          this.setShrunk(true)
        }

        if (this.token) {
          this.getNotificationsCount().then(({ data }) => {
            this.notifyList = data || {}
          })

          if (this.featureAccess('ALERTS')) {
            this.getNotificationList({ $skip: 0, $limit: 1001, read: false, $project: 0, category: 'inbox' }).then((data) => {
              if (data?.count) {
                this.SET_NOTIFICATION_COUNT(data?.count)
              } else {
                this.SET_NOTIFICATION_COUNT(0)
              }
            })
          }

          if (this.userData.id) {
            if (!this.userData.preferredCurrency) {
              await this.setFiat({ preferredCurrency: 'USD' })
            }

            if (!this.userData.scoringFormula) {
              await this.setScoringFormula({ formula: appConfig.VUE_APP_SCORING_FORMULA })
            }
          }
        }
      },
      immediate: true,
    },
  },
  mounted() {
    if (this.token && this.featureAccess('ALERTS')) {
      this.getNotificationList({ $skip: 0, $limit: 1001, read: false, $project: 0, category: 'inbox' }).then((data) => {
        if (data?.count) {
          this.SET_NOTIFICATION_COUNT(data?.count)
        } else {
          this.SET_NOTIFICATION_COUNT(0)
        }
      })
      this.alertsGettingInterval()
    }
    if (this.isMobile || this.isTablet) {
      this.setShrunk(true)
    }

    // Check for main overflow and scrollbar is hit the bottom
    this.checkMainOverflow()

    const mainEl = this.$el?.querySelector
      ? this.$el.querySelector('.side-bar__main')
      : null
    if (mainEl) {
      this.resizeObserver = new ResizeObserver(() => {
        this.checkMainOverflow()
      })
      this.resizeObserver.observe(mainEl)

      mainEl.addEventListener('scroll', this.handleScroll)
      this.handleScroll({ target: mainEl })
    }

    window.addEventListener('resize', this.checkMainOverflow)
  },
  beforeDestroy() {
    this.resizeObserver?.disconnect()
    window.removeEventListener('resize', this.checkMainOverflow)

    const mainEl = this.$el?.querySelector
      ? this.$el.querySelector('.side-bar__main')
      : null
    mainEl?.removeEventListener('scroll', this.handleScroll)

    clearTimeout(this.alertInterval)
  },
  methods: {
    ...mapActions('user', ['setFiat', 'setScoringFormula']),
    ...mapActions('sidebar', ['getNotificationsCount', 'setShrunkPreference']),
    ...mapActions('alerts', ['getNotificationList']),
    ...mapMutations({
      setShrunk: 'sidebar/SET_SHRUNK',
      SET_NOTIFICATION_COUNT: 'alerts/SET_NOTIFICATION_COUNT'
    }),
    alertsGettingInterval(interval = 10000) {
      this.alertInterval = setTimeout(() => {
        this.getNotificationList({ $skip: 0, $limit: 1001, read: false, $project: 0, category: 'inbox' }).then((data) => {
          if (data?.count) {
            this.SET_NOTIFICATION_COUNT(data?.count)
          } else {
            this.SET_NOTIFICATION_COUNT(0)
          }
        }).finally(() => this.alertsGettingInterval())
      }, interval);
    },
    hasNotify(entity) {
      return this.notifyList[entity] > 0
    },
    openMobileCases() {
      this.$root.$emit("toggle-toolbar-right-shrunk", false);
      this.$root.$emit('toggle-view-menu', false)
      this.$root.$emit('toggle-cases-menu', true)
      this.$root.$emit('toggle-toolbar-bottom', false)
    },
    openMobileViewBar() {
      this.$root.$emit("toggle-toolbar-right-shrunk", false);
      this.$root.$emit('toggle-cases-menu', false)
      this.$root.$emit('toggle-toolbar-bottom', false)
      this.$root.$emit('toggle-view-menu', true)
    },
    openMobileSearchOnGraphBar() {
      this.$root.$emit("toggle-toolbar-right-shrunk", false);
      this.$root.$emit('toggle-cases-menu', false)
      this.$root.$emit('toggle-view-menu', false)
      this.$root.$emit('toggle-toolbar-bottom', true)
    },
    checkMainOverflow() {
      this.$nextTick(() => {
        const main = this.$el?.querySelector
          ? this.$el.querySelector('.side-bar__main')
          : null

        if (main) {
          this.isOverflow = main.scrollHeight > main.clientHeight
        }
      })
    },
    handleScroll(e) {
      const el = e.target
      const scrolledToBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1

      this.isScrolledToBottom = scrolledToBottom
    },

    toggleSection(sectionKey) {
      this.openedSections = {
        ...this.openedSections,
        [sectionKey]: !this.openedSections[sectionKey],
      }

      localStorage.setItem(
        SIDEBAR_SECTIONS_STORAGE_KEY,
        JSON.stringify(this.openedSections),
      )

      this.checkMainOverflow()
    },
  }
};
</script>

<style scoped>
.side-bar {
  width: 200px;
  height: calc(100svh - var(--disclaimer-height));
  transition: width 0.2s;
  background: #fff;
  box-shadow: 14px 0 31px -9px rgba(0,0,0,0.46);
  overflow: hidden;
  position: relative;
  border-right: 1px solid var(--Basic-200);
}

.side-bar__shrunk {
  width: 56px;
  transition: width 0.2s;
}

.side-bar__header {
  display: flex;
  height: 72px;
  align-items: center;
  justify-content: space-between;
  padding: 4px 16px 0 16px;
  border-bottom: 1px solid #e3e3e3;
  position: relative;
}

.side-bar__header--short {
  justify-content: center;
  padding: 0;
}

.side-bar__header svg {
  margin-bottom: 1px;
}

.side-bar__footer {
  padding-top: 8px;
}

.sidebar-open-icon__shrunk {
  margin-left: -3px;
}

.logo-wrap {
  max-width: fit-content;
}

.logo-wrap svg {
  display: block;
  width: 100%;
}

.default-logo {
  width: 110px !important;
}

.side-bar__main--overflow:after {
  content: '';
  opacity: 1;
  margin-top: -32px;
  position: sticky;
  bottom: 0;
  height: 32px;
  width: 100%;
  pointer-events: none;
  background: linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.05));
  display: block;
  transition: opacity 0.3s ease;
}
.side-bar__main--scrolled-to-bottom:after {
  opacity: 0;
}

.side-bar__wrap {
  display: flex;
  flex-direction: column;
  height: calc(100% - 72px);
  overflow: hidden;
}

.side-bar__main {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: 6px;
  position: relative;
  scrollbar-gutter: auto;
}

.side-bar__main--overflow .side-bar__section {
  margin-right: -4px;
}

.side-bar__main::-webkit-scrollbar {
  width: 4px;
}

.side-bar__main::-webkit-scrollbar-track {
  background-color: transparent !important;
  box-shadow: none !important;
  border: none !important;
}

.side-bar__main::-webkit-scrollbar-thumb {
  background-color: var(--Basic-200) !important;
  border-radius: 4px !important;
  box-shadow: none !important;
}

.side-bar__shrunk .side-bar__section {
  padding: 8px;
}

.side-bar__footer {
  flex: 0 0 auto;
  padding-top: 8px;
  padding-left: 8px;
  padding-right: 8px;
}

@media (max-width: 767px) {
  .side-bar {
    position: absolute;
    width: 100vw;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 999;
    box-shadow: none;
  }
  .side-bar__shrunk {
    overflow: unset;
    height: 57px;
  }
  .side-bar__header {
    height: 57px;
  }
  .side-bar__wrap {
    height: calc(100% - 57px);
  }
}
</style>
